// Gedeelde definities voor het financiële leerlingenrapport: dezelfde kolommen en waarden voeden
// de tabel, de zoekfunctie en de Excel-export, zodat die drie nooit uit elkaar lopen.
import type { TFunction } from "i18next";
import { calculateAge } from "../../utils/dateUtils";
import { formatDutchDate } from "../../utils/dutchDate";
import { classLabel } from "./classLabel";

export type FinanceWarning = "NO_FAMILY" | "MULTIPLE_FAMILIES" | "SEPARATE_HOUSEHOLDS" | "NO_BANK_ACCOUNT";

export type FinanceParent = {
  parentId: string;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  zipCode?: string | null;
  city?: string | null;
  accountHolderName?: string | null;
  iban?: string | null;
};

export type FinanceStudentRow = {
  childId: string;
  firstName?: string | null;
  lastName?: string | null;
  gender?: string | null;
  dateBirth?: string | null;
  dateRegistration?: string | null;
  firstStartDate?: string | null;
  withdrawnAt?: string | null;
  status: "ACTIVE" | "WITHDRAWN";
  className?: string | null;
  families?: Array<{ familyId: string; name?: string | null }>;
  parents?: FinanceParent[];
  warnings?: FinanceWarning[];
};

export type FinanceStudentReport = {
  schoolYear?: { id: string; startDate?: string; endDate?: string; monthlyFee?: number | null } | null;
  /** Period the report covers, after defaults (current school year up to today). */
  period?: { from: string; to: string } | null;
  fullIbanIncluded: boolean;
  rows: FinanceStudentRow[];
  /** School years to choose from, newest first (1 August – 31 July). */
  schoolYears?: Array<{ id: string; startDate: string; endDate: string; monthlyFee?: number | null }>;
};

export type ColumnKind = "childName" | "date" | "text" | "email" | "phone" | "iban" | "warnings" | "status";

export type FinanceColumn = {
  key: string;
  /** Full label: column picker, Excel header. */
  label: string;
  /** Label under the group heading in the on-screen and printed table. */
  shortLabel: string;
  group: string;
  kind: ColumnKind;
  defaultVisible: boolean;
  value: (row: FinanceStudentRow) => string;
};

const PARENT_FIELDS = ["name", "email", "phone", "address", "accountHolder", "iban"] as const;
type ParentField = (typeof PARENT_FIELDS)[number];
const DEFAULT_PARENT_FIELDS: ParentField[] = ["name", "email", "phone", "iban"];

export const MIN_PARENT_SLOTS = 2;
// v2: the table shows one block per group; earlier saved choices start again from the defaults.
export const COLUMN_STORAGE_KEY = "lumen.finance.students.columns.v2";

export const joinName = (first?: string | null, last?: string | null): string =>
  [first, last].filter((part) => part && String(part).trim()).join(" ").trim();

export const familyNames = (row: FinanceStudentRow): string[] =>
  (row.families || []).map((family) => family.name || "").filter(Boolean);

export const childNameWithAge = (row: FinanceStudentRow, asOf: Date = new Date()): string => {
  const name = joinName(row.firstName, row.lastName);
  const age = calculateAge(row.dateBirth, asOf);
  return age === undefined ? name : `${name} (${age})`;
};

const parentAddress = (parent?: FinanceParent): string => {
  if (!parent) return "";
  const place = [parent.zipCode, parent.city].filter(Boolean).join(" ");
  return [parent.address, place].filter(Boolean).join(", ");
};

const parentValue = (parent: FinanceParent | undefined, field: ParentField): string => {
  if (!parent) return "";
  switch (field) {
    case "name": return joinName(parent.firstName, parent.lastName);
    case "email": return parent.email || "";
    case "phone": return parent.phone || "";
    case "address": return parentAddress(parent);
    case "accountHolder": return parent.accountHolderName || "";
    case "iban": return parent.iban || "";
    default: return "";
  }
};

export const warningLabel = (t: TFunction, warning: FinanceWarning): string => {
  switch (warning) {
    case "NO_FAMILY":
      return t("finance.students_report.warnings.no_family", { defaultValue: "No family linked" });
    case "MULTIPLE_FAMILIES":
      return t("finance.students_report.warnings.multiple_families", { defaultValue: "Linked to several families" });
    case "SEPARATE_HOUSEHOLDS":
      return t("finance.students_report.warnings.separate_households", {
        defaultValue: "Separate households – payment split not recorded",
      });
    case "NO_BANK_ACCOUNT":
      return t("finance.students_report.warnings.no_bank_account", { defaultValue: "No approved bank account" });
    default:
      return warning;
  }
};

export const statusLabel = (t: TFunction, row: FinanceStudentRow): string =>
  row.status === "WITHDRAWN"
    ? t("finance.students_report.status.withdrawn", { defaultValue: "Withdrawn" })
    : t("finance.students_report.status.active", { defaultValue: "Active" });

export const parentSlotCount = (rows: FinanceStudentRow[]): number =>
  Math.max(MIN_PARENT_SLOTS, ...rows.map((row) => row.parents?.length ?? 0));

export function buildColumns(t: TFunction, parentSlots: number = MIN_PARENT_SLOTS): FinanceColumn[] {
  const childGroup = t("finance.students_report.groups.child", { defaultValue: "Child" });
  const datesGroup = t("finance.students_report.groups.dates", { defaultValue: "Dates" });
  const childColumns: Array<Omit<FinanceColumn, "shortLabel"> & { shortLabel?: string }> = [
    {
      key: "child.name", group: childGroup, kind: "childName", defaultVisible: true,
      label: t("finance.students_report.columns.child", { defaultValue: "Child" }),
      value: (row) => childNameWithAge(row),
    },
    {
      key: "child.dateBirth", group: datesGroup, kind: "date", defaultVisible: true,
      shortLabel: t("finance.students_report.short.born", { defaultValue: "Born" }),
      label: t("finance.students_report.columns.date_birth", { defaultValue: "Date of birth" }),
      value: (row) => formatDutchDate(row.dateBirth, ""),
    },
    {
      key: "child.dateRegistration", group: datesGroup, kind: "date", defaultVisible: true,
      shortLabel: t("finance.students_report.short.registered", { defaultValue: "Registered" }),
      label: t("finance.students_report.columns.date_registration", { defaultValue: "Registration date" }),
      value: (row) => formatDutchDate(row.dateRegistration, ""),
    },
    {
      key: "child.firstStart", group: datesGroup, kind: "date", defaultVisible: true,
      shortLabel: t("finance.students_report.short.started", { defaultValue: "Started" }),
      label: t("finance.students_report.columns.first_start", { defaultValue: "First school day" }),
      value: (row) => formatDutchDate(row.firstStartDate, ""),
    },
    {
      key: "child.withdrawnAt", group: datesGroup, kind: "date", defaultVisible: true,
      shortLabel: t("finance.students_report.short.withdrawn", { defaultValue: "Withdrawn" }),
      label: t("finance.students_report.columns.withdrawn_at", { defaultValue: "Withdrawn on" }),
      value: (row) => formatDutchDate(row.withdrawnAt, ""),
    },
    {
      key: "child.status", group: childGroup, kind: "status", defaultVisible: false,
      label: t("finance.students_report.columns.status", { defaultValue: "Status" }),
      value: (row) => statusLabel(t, row),
    },
    {
      key: "child.class", group: childGroup, kind: "text", defaultVisible: true,
      label: t("finance.students_report.columns.class", { defaultValue: "Class" }),
      value: (row) => (row.className ? classLabel(t, { className: row.className }) : ""),
    },
    {
      key: "family.names", group: childGroup, kind: "text", defaultVisible: true,
      label: t("finance.students_report.columns.family", { defaultValue: "Family" }),
      value: (row) => familyNames(row).join(", "),
    },
  ];
  const columns: FinanceColumn[] = childColumns.map((column) => ({ ...column, shortLabel: column.shortLabel ?? column.label }));

  for (let slot = 1; slot <= parentSlots; slot += 1) {
    const group = t("finance.students_report.groups.parent", { number: slot, defaultValue: `Parent ${slot}` });
    const fieldLabels: Record<ParentField, string> = {
      name: t("finance.students_report.columns.parent_name", { defaultValue: "Name" }),
      email: t("finance.students_report.columns.parent_email", { defaultValue: "Email" }),
      phone: t("finance.students_report.columns.parent_phone", { defaultValue: "Phone" }),
      address: t("finance.students_report.columns.parent_address", { defaultValue: "Address" }),
      accountHolder: t("finance.students_report.columns.account_holder", { defaultValue: "Account holder" }),
      iban: t("finance.students_report.columns.iban", { defaultValue: "IBAN" }),
    };
    for (const field of PARENT_FIELDS) {
      columns.push({
        key: `parent${slot}.${field}`,
        group,
        kind: field === "iban" ? "iban" : field === "email" ? "email" : field === "phone" ? "phone" : "text",
        defaultVisible: slot <= MIN_PARENT_SLOTS && DEFAULT_PARENT_FIELDS.includes(field),
        label: `${group} – ${fieldLabels[field]}`,
        shortLabel: fieldLabels[field],
        value: (row) => parentValue(row.parents?.[slot - 1], field),
      });
    }
  }

  const warningsLabel = t("finance.students_report.columns.warnings", { defaultValue: "Attention points" });
  columns.push({
    key: "warnings",
    shortLabel: warningsLabel,
    group: t("finance.students_report.groups.other", { defaultValue: "Other" }),
    kind: "warnings",
    defaultVisible: true,
    label: warningsLabel,
    value: (row) => (row.warnings || []).map((warning) => warningLabel(t, warning)).join("; "),
  });
  return columns;
}

/** Masked IBANs arrive as 16 stars plus the last four digits; on screen and on paper only the last four matter. */
export const compactIban = (iban?: string | null): string => {
  if (!iban) return "";
  return iban.startsWith("*") ? `•••• ${iban.replace(/^\*+/, "")}` : iban;
};

export type BlockKind = "child" | "dates" | "parent" | "warnings";

export type FinanceBlock = {
  group: string;
  kind: BlockKind;
  /** 1-based parent number for parent blocks. */
  parentSlot: number;
  columns: FinanceColumn[];
};

const blockKindOf = (column: FinanceColumn): BlockKind => {
  if (column.key.startsWith("parent")) return "parent";
  if (column.key === "warnings") return "warnings";
  if (column.kind === "date") return "dates";
  return "child";
};

/** The table shows one cell per group (child, dates, each parent, attention points) holding the chosen fields. */
export function buildBlocks(columns: FinanceColumn[]): FinanceBlock[] {
  const blocks: FinanceBlock[] = [];
  columns.forEach((column) => {
    const existing = blocks.find((block) => block.group === column.group);
    if (existing) {
      existing.columns.push(column);
      return;
    }
    blocks.push({
      group: column.group,
      kind: blockKindOf(column),
      parentSlot: Number(column.key.match(/^parent(\d+)\./)?.[1] ?? 0),
      columns: [column],
    });
  });
  return blocks;
}

export const defaultColumnKeys = (columns: FinanceColumn[]): string[] =>
  columns.filter((column) => column.defaultVisible).map((column) => column.key);

/** Search in everything a reader may look for, visible or not; IBANs only through what is shown (last digits). */
export function matchesSearch(row: FinanceStudentRow, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  const haystack = [
    joinName(row.firstName, row.lastName),
    row.className,
    // Also find "Kindergarten" by "kleuterklas" and "Grade 3" by "klas 3", as the list shows them.
    row.className?.replace(/\bkindergarten\b/gi, "kleuterklas").replace(/\bgrade\b/gi, "klas"),
    ...familyNames(row),
    ...(row.parents || []).flatMap((parent) => [
      joinName(parent.firstName, parent.lastName),
      parent.email,
      parent.phone,
      parentAddress(parent),
      parent.accountHolderName,
      parent.iban,
    ]),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return needle.split(/\s+/).every((part) => haystack.includes(part));
}

export function loadVisibleColumns(available: FinanceColumn[]): string[] {
  const fallback = defaultColumnKeys(available);
  try {
    const raw = window.localStorage.getItem(COLUMN_STORAGE_KEY);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return fallback;
    const known = new Set(available.map((column) => column.key));
    const kept = parsed.filter((key): key is string => typeof key === "string" && known.has(key));
    return kept.length ? kept : fallback;
  } catch {
    return fallback;
  }
}

export function saveVisibleColumns(keys: string[]): void {
  try {
    window.localStorage.setItem(COLUMN_STORAGE_KEY, JSON.stringify(keys));
  } catch {
    // Opslaan van de kolomkeuze is een gemak; zonder browseropslag werkt de pagina met de standaardkolommen.
  }
}

export function resetVisibleColumns(): void {
  try {
    window.localStorage.removeItem(COLUMN_STORAGE_KEY);
  } catch {
    // zie saveVisibleColumns
  }
}
